import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Shield, AlertTriangle, Loader2, KeyRound } from "lucide-react";
import { useAuth, api } from "../App";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ee59bb71-25b5-4f3e-ade6-7b45b4f149b6/artifacts/v205dw0b_IMG_7633.png";
const HERO_IMAGE = "https://images.unsplash.com/photo-1490818387583-1baba5e638af?w=800&h=1200&fit=crop";

export default function Login() {
  const [showEmergency, setShowEmergency] = useState(false);
  const [emergencyEmail, setEmergencyEmail] = useState("");
  const [emergencyPassword, setEmergencyPassword] = useState("");
  const [emergencyLoading, setEmergencyLoading] = useState(false);
  const [emergencyError, setEmergencyError] = useState(null);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmergencyLogin = async (e) => {
    e.preventDefault();
    setEmergencyLoading(true);
    setEmergencyError(null);
    try {
      const resp = await api.post("/auth/emergency-login", {
        email: emergencyEmail.trim().toLowerCase(),
        password: emergencyPassword,
      });
      if (resp.data?.token) {
        localStorage.setItem("session_token", resp.data.token);
      }
      setUser(resp.data);
      toast.success("Emergency login successful");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setEmergencyError(err.response?.data?.detail || "Login failed");
    } finally {
      setEmergencyLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full overflow-hidden" data-testid="login-page">
      {/* Mobile: Stack vertically, Desktop: Side by side */}
      <div className="flex flex-col lg:grid lg:grid-cols-2 min-h-screen">
        
        {/* Left Side - Login Form */}
        <div className="flex flex-col justify-center items-center p-6 sm:p-8 lg:p-12 bg-white min-h-[60vh] lg:min-h-screen order-2 lg:order-1">
          <div className="w-full max-w-sm space-y-6 sm:space-y-8">
            {/* Logo */}
            <div className="flex flex-col items-center text-center">
              <img 
                src={LOGO_URL} 
                alt="Nutrition Solutions For Me" 
                className="w-20 h-20 sm:w-24 sm:h-24 mb-4"
              />
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-forest tracking-tight">
                Welcome Back
              </h1>
              <p className="text-slate-500 mt-2 text-sm sm:text-base">
                Sign in to manage your patients' meal plans
              </p>
            </div>

            {/* Login Button */}
            <div className="space-y-4">
              <Button
                onClick={handleGoogleLogin}
                className="w-full h-12 sm:h-14 bg-white border-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-forest/30 rounded-full font-medium transition-colors flex items-center justify-center gap-3 text-sm sm:text-base"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>
            </div>

            {/* Emergency Access Toggle */}
            <div className="text-center">
              <button
                onClick={() => setShowEmergency(!showEmergency)}
                className="text-xs text-slate-400 hover:text-forest transition-colors inline-flex items-center gap-1"
                data-testid="emergency-access-toggle"
              >
                <KeyRound className="w-3 h-3" />
                {showEmergency ? "Hide" : "Admin Emergency Access"}
              </button>
            </div>

            {/* Emergency Login Form */}
            {showEmergency && (
              <form onSubmit={handleEmergencyLogin} className="space-y-3 p-4 bg-amber-50/50 border border-amber-200 rounded-xl" data-testid="emergency-login-form">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <span className="text-xs font-semibold text-amber-800">Emergency Admin Access Only</span>
                </div>
                <input
                  type="email"
                  placeholder="Admin email"
                  value={emergencyEmail}
                  onChange={(e) => setEmergencyEmail(e.target.value)}
                  className="w-full h-10 px-3 text-sm border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest/30 bg-white"
                  required
                  data-testid="emergency-email-input"
                />
                <input
                  type="password"
                  placeholder="Emergency password"
                  value={emergencyPassword}
                  onChange={(e) => setEmergencyPassword(e.target.value)}
                  className="w-full h-10 px-3 text-sm border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest/30 bg-white"
                  required
                  data-testid="emergency-password-input"
                />
                {emergencyError && (
                  <p className="text-xs text-red-600" data-testid="emergency-login-error">{emergencyError}</p>
                )}
                <Button
                  type="submit"
                  disabled={emergencyLoading}
                  className="w-full h-10 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium"
                  data-testid="emergency-login-submit"
                >
                  {emergencyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Emergency Sign In"}
                </Button>
              </form>
            )}

            {/* HIPAA Notice */}
            <div className="bg-green-50 border border-green-100 rounded-lg p-3 sm:p-4">
              <div className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs sm:text-sm font-medium text-green-800">HIPAA Compliant</p>
                  <p className="text-xs text-green-700 mt-0.5">
                    Your data is encrypted and secured per HIPAA regulations
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <p className="text-center text-xs text-slate-400 px-4">
              By signing in, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>

        {/* Right Side - Hero Image */}
        <div className="relative h-[40vh] lg:h-auto order-1 lg:order-2">
          <img
            src={HERO_IMAGE}
            alt="Fresh healthy vegetables and fruits"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-forest/60 via-forest/20 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 lg:bottom-12 lg:left-12 lg:right-12 text-white">
            <h2 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold mb-1 sm:mb-2">
              Personalized Nutrition Plans
            </h2>
            <p className="text-white/90 text-sm sm:text-base lg:text-lg">
              Create customized meal plans for your patients based on their dietary needs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
